/**
 * Vanilla-DOM preorder modal (payment-option picker). Zero runtime
 * dependencies and SSR-safe: no DOM access happens at module load, and the
 * single-option fast path resolves without touching the DOM at all.
 *
 * Behavior mirrors the theme app embed's preorder modal
 * (shopify/extensions/src/theme-app-extension/assets/components/PreorderModal.js
 * + assets/js/integrations/hijack.js): same modal-vs-fast-path decision,
 * payment-option sorting/copy, quantity validation messages, acknowledgement
 * gate, and the documented `stoq:preorder-modal:*` /
 * `stoq:preorder-payment-option:selected` events. Differences are documented
 * in docs/preorder-modal.md.
 */
import {
  emit,
  EVENT_PREORDER_MODAL_CLOSED,
  EVENT_PREORDER_MODAL_OPENED,
  EVENT_PREORDER_PAYMENT_OPTION_SELECTED,
} from '../events'
import { el, trapFocus } from '../modal/dom'
import { resolveStyles } from '../modal/texts'
import { toNumericId } from '../util'
import { injectPreorderStyles } from './dom'
import {
  acknowledgementText,
  buildPreorderLine,
  clampQuantity,
  formattedShippingText,
  needsModal,
  quantityBounds,
  quantityLimitText,
  resolvePaymentOptions,
  resolvePreorderTexts,
  translatedPlanValue,
  validateQuantity,
} from './logic'
import type {
  PreorderConfirmation,
  PreorderContext,
  PreorderModalOptions,
  PreorderPaymentOption,
} from './types'

export type {
  PreorderConfirmation,
  PreorderContext,
  PreorderButtonContent,
  PreorderModalOptions,
  PreorderModalTexts,
  PreorderPaymentOption,
} from './types'
export {
  acknowledgementText,
  buildButtonContent,
  buildPreorderLine,
  clampQuantity,
  DEFAULT_PREORDER_TEXTS,
  discountValue,
  formatAmount,
  formattedShippingText,
  needsModal,
  parseTemplate,
  paymentValue,
  quantityBounds,
  quantityLimitText,
  rawPaymentOptions,
  resolvePaymentOptions,
  resolvePreorderTexts,
  translatedPlanValue,
  validateQuantity,
} from './logic'

let uid = 0

/** The currently open preorder modal (at most one at a time). */
let activeClose: (() => void) | null = null

/**
 * Event payload mirroring the embed's `productData` argument
 * (api_docs/custom-events.mdx) — built from the options the caller passed,
 * missing fields are null.
 */
function buildProductData(options: PreorderModalOptions, variantId: number) {
  return {
    id: options.productId !== undefined ? safeId(options.productId) : null,
    title: options.productTitle ?? null,
    featured_image: options.imageUrl ?? null,
    variants: [{ id: variantId, title: options.variantTitle ?? null }],
  }
}

function safeId(value: number | string): number | null {
  try {
    return toNumericId(value)
  } catch {
    return null
  }
}

function buildConfirmation(
  ctx: PreorderContext,
  quantity: number,
  chosen: PreorderPaymentOption | null,
  acknowledged: boolean,
): PreorderConfirmation {
  const sellingPlanId = chosen?.sellingPlanId ?? ctx.state.sellingPlanId
  return {
    cartLine: buildPreorderLine(ctx.plan, ctx.variantId, quantity, sellingPlanId),
    sellingPlanId,
    quantity,
    paymentOption: chosen,
    acknowledged,
  }
}

/**
 * Opens the preorder modal (or resolves immediately when no UI is needed —
 * see docs/preorder-modal.md). Resolves with the {@link PreorderConfirmation}
 * when the shopper confirms, or `null` when they dismiss. `onConfirm` is
 * invoked (in addition to the promise resolving) on confirmation.
 */
export function openPreorderModal(
  ctx: PreorderContext,
  options: PreorderModalOptions,
): Promise<PreorderConfirmation | null> {
  const { plan, state, settings, locale, variantId } = ctx
  const paymentOptions = resolvePaymentOptions(plan, settings, locale)
  const bounds = quantityBounds(plan, state)
  const initialQuantity = clampQuantity(options.quantity ?? bounds.min, bounds)
  const requestedQuantity = options.quantity !== undefined
    ? Math.floor(Number(options.quantity)) || initialQuantity
    : initialQuantity
  const initialError = validateQuantity(requestedQuantity, plan, state)

  // Fast path (mirrors the embed's shouldShowModal): a single payment option,
  // no acknowledgement requirement and a valid quantity need no UI — resolve
  // immediately without rendering (and without opened/closed events, exactly
  // like the embed, which skips its modal in this case). DOM-free, so this
  // path is also SSR-safe.
  if (!needsModal(plan, paymentOptions) && initialError === null) {
    const confirmation = buildConfirmation(
      ctx,
      initialQuantity,
      paymentOptions[0] ?? null,
      false,
    )
    options.onConfirm?.(confirmation)
    return Promise.resolve(confirmation)
  }

  if (typeof document === 'undefined') {
    return Promise.reject(
      new Error('[Stoq] openPreorderModal requires a browser environment (no document found)'),
    )
  }

  return new Promise((resolve) => {
    renderModal(ctx, options, paymentOptions, requestedQuantity, initialError, resolve)
  })
}

function renderModal(
  ctx: PreorderContext,
  options: PreorderModalOptions,
  paymentOptions: PreorderPaymentOption[],
  initialQuantity: number,
  initialError: string | null,
  resolve: (confirmation: PreorderConfirmation | null) => void,
): void {
  const { plan, state, settings, locale, variantId } = ctx
  const doc = document
  const id = ++uid

  activeClose?.()
  injectPreorderStyles(doc)

  const texts = resolvePreorderTexts(plan, settings, locale, options.texts)
  const styles = resolveStyles(settings, options.styles)
  const bounds = quantityBounds(plan, state)
  const productData = buildProductData(options, variantId)
  const requiresAcknowledgement = plan.require_preorder_acknowledgement === true

  // Mutable modal state.
  let quantity = initialQuantity
  let error = initialError
  let selectedId = paymentOptions[0]?.sellingPlanId ?? null
  let acknowledged = false
  let settled = false

  const overlay = el(doc, 'div', { className: 'stoq-preorder-overlay' })
  overlay.style.zIndex = styles.zIndex

  const dialog = el(doc, 'div', {
    className: 'stoq-preorder-modal',
    attrs: {
      role: 'dialog',
      'aria-modal': 'true',
      'aria-labelledby': `stoq-preorder-header-${id}`,
      tabindex: '-1',
    },
  })
  dialog.style.color = styles.textColor
  dialog.style.backgroundColor = styles.backgroundColor
  dialog.style.borderRadius = styles.borderRadius
  dialog.style.fontFamily = styles.fontFamily

  const previousActive = doc.activeElement

  const teardown = (): void => {
    if (activeClose === close) activeClose = null
    doc.removeEventListener('keydown', onKeydown, true)
    overlay.remove()
    if (previousActive instanceof HTMLElement) previousActive.focus()
  }

  const reject = (): void => {
    if (settled) return
    settled = true
    teardown()
    emit(EVENT_PREORDER_MODAL_CLOSED, {
      action: 'rejected',
      productData,
      variantId,
      selectedSellingPlanId: null,
      sellingPlan: plan,
    })
    resolve(null)
  }
  const close = reject

  const confirm = (): void => {
    if (settled) return
    error = validateQuantity(quantity, plan, state)
    if (error !== null) {
      update()
      return
    }
    if (requiresAcknowledgement && !acknowledged) return
    settled = true
    teardown()

    const chosen = paymentOptions.find((option) => option.sellingPlanId === selectedId)
      ?? paymentOptions[0]
      ?? null
    const confirmation = buildConfirmation(ctx, quantity, chosen, requiresAcknowledgement && acknowledged)
    emit(EVENT_PREORDER_MODAL_CLOSED, {
      action: 'accepted',
      productData,
      variantId,
      selectedSellingPlanId: confirmation.sellingPlanId,
      sellingPlan: plan,
      quantity: confirmation.quantity,
      acknowledgedPreorder: confirmation.acknowledged,
    })
    options.onConfirm?.(confirmation)
    resolve(confirmation)
  }

  const onKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      event.preventDefault()
      reject()
      return
    }
    trapFocus(dialog, event)
  }

  // --- Header -------------------------------------------------------------
  dialog.appendChild(el(doc, 'h2', {
    className: 'stoq-preorder-header',
    text: texts.title,
    attrs: { id: `stoq-preorder-header-${id}` },
  }))

  const closeButton = el(doc, 'button', {
    className: 'stoq-preorder-close',
    text: '×',
    attrs: { type: 'button', 'aria-label': texts.closeLabel },
  })
  closeButton.addEventListener('click', reject)
  dialog.appendChild(closeButton)

  // --- Error banner -------------------------------------------------------
  const errorEl = el(doc, 'div', {
    className: 'stoq-preorder-error',
    attrs: { 'aria-live': 'polite' },
  })
  dialog.appendChild(errorEl)

  // --- Product row + quantity stepper --------------------------------------
  const productRow = el(doc, 'div', { className: 'stoq-preorder-product' })
  if (options.imageUrl !== undefined && options.imageUrl !== '') {
    productRow.appendChild(el(doc, 'img', {
      className: 'stoq-preorder-product-image',
      attrs: { src: options.imageUrl, alt: options.productTitle ?? '' },
    }))
  }
  const productInfo = el(doc, 'div', { className: 'stoq-preorder-product-info' })
  if (options.productTitle !== undefined && options.productTitle !== '') {
    productInfo.appendChild(el(doc, 'div', {
      className: 'stoq-preorder-product-title',
      text: options.productTitle,
    }))
  }
  if (options.variantTitle !== undefined && options.variantTitle !== '') {
    productInfo.appendChild(el(doc, 'div', {
      className: 'stoq-preorder-variant-title',
      text: options.variantTitle,
    }))
  }
  productRow.appendChild(productInfo)

  const qtyWrap = el(doc, 'div', { className: 'stoq-preorder-qty' })
  const decrement = el(doc, 'button', {
    className: 'stoq-preorder-qty-button',
    text: '−',
    attrs: { type: 'button', 'aria-label': texts.decreaseLabel },
  })
  const qtyInput = el(doc, 'input', {
    className: 'stoq-preorder-qty-input',
    attrs: {
      type: 'number',
      min: String(bounds.min),
      'aria-label': texts.quantityLabel,
    },
  })
  qtyInput.value = String(quantity)
  const increment = el(doc, 'button', {
    className: 'stoq-preorder-qty-button',
    text: '+',
    attrs: { type: 'button', 'aria-label': texts.increaseLabel },
  })
  qtyWrap.appendChild(decrement)
  qtyWrap.appendChild(qtyInput)
  qtyWrap.appendChild(increment)
  productRow.appendChild(qtyWrap)
  dialog.appendChild(productRow)

  decrement.addEventListener('click', () => {
    quantity = clampQuantity(quantity - 1, bounds)
    error = validateQuantity(quantity, plan, state)
    update()
  })
  increment.addEventListener('click', () => {
    quantity = clampQuantity(quantity + 1, bounds)
    error = validateQuantity(quantity, plan, state)
    update()
  })
  qtyInput.addEventListener('input', () => {
    // Typed input clamps only at the minimum (like the embed); values above
    // the limit surface a validation error and disable the confirm button.
    const parsed = Math.floor(Number(qtyInput.value))
    quantity = Number.isFinite(parsed) && parsed > 0 ? Math.max(bounds.min, parsed) : bounds.min
    error = validateQuantity(quantity, plan, state)
    update({ skipInput: true })
  })
  qtyInput.addEventListener('change', () => {
    quantity = Math.max(bounds.min, Math.floor(Number(qtyInput.value)) || bounds.min)
    error = validateQuantity(quantity, plan, state)
    update()
  })

  // --- Disclaimer + details -------------------------------------------------
  if (plan.preorder_button_description_enabled !== false) {
    const disclaimer = translatedPlanValue(plan, 'preorder_button_description', settings, locale)
    if (disclaimer !== null && disclaimer.trim() !== '') {
      // Merchant-configured HTML, same as the embed.
      dialog.appendChild(el(doc, 'div', {
        className: 'stoq-preorder-disclaimer',
        html: disclaimer,
      }))
    }
  }

  const details: string[] = []
  const limitText = quantityLimitText(plan, state, settings, locale)
  if (limitText !== null) details.push(limitText)
  if (plan.preorder_button_description_show_shipping === true) {
    const shipping = formattedShippingText(plan, state.shippingText, settings, locale)
    if (shipping !== null) details.push(shipping)
  }
  if (details.length > 0) {
    const list = el(doc, 'ul', { className: 'stoq-preorder-details' })
    for (const detail of details) {
      // Merchant-configured copy may contain markup (the embed renders it
      // via dangerouslySetInnerHTML too).
      list.appendChild(el(doc, 'li', { html: detail }))
    }
    dialog.appendChild(list)
  }

  // --- Payment options ------------------------------------------------------
  let paymentWidget: HTMLElement | null = null
  const optionRoots = new Map<number | null, HTMLElement>()
  if (paymentOptions.length > 1) {
    dialog.appendChild(el(doc, 'h3', {
      className: 'stoq-preorder-payment-heading',
      text: texts.paymentHeading,
    }))
    paymentWidget = el(doc, 'div', {
      className: 'stoq-preorder-payment',
      attrs: { role: 'radiogroup', 'aria-label': texts.paymentHeading },
    })

    paymentOptions.forEach((option, index) => {
      const optionRoot = el(doc, 'label', { className: 'stoq-preorder-payment-option' })
      const row = el(doc, 'div', { className: 'stoq-preorder-payment-option-row' })

      const radio = el(doc, 'input', {
        className: 'stoq-preorder-payment-input',
        attrs: {
          type: 'radio',
          id: `stoq-preorder-payment-${id}-${index}`,
          name: `stoq-preorder-option-${id}`,
          value: option.sellingPlanId !== null ? String(option.sellingPlanId) : '',
        },
      })
      radio.checked = option.sellingPlanId === selectedId
      radio.addEventListener('change', () => {
        if (!radio.checked || option.sellingPlanId === selectedId) return
        selectedId = option.sellingPlanId
        update()
        // Documented event: dispatched on the payment widget element and
        // bubbles to window (api_docs/custom-events.mdx).
        if (paymentWidget && typeof CustomEvent !== 'undefined') {
          paymentWidget.dispatchEvent(new CustomEvent(EVENT_PREORDER_PAYMENT_OPTION_SELECTED, {
            detail: { paymentWidget, variantId, selectedSellingPlanId: option.sellingPlanId },
            bubbles: true,
          }))
        }
      })
      row.appendChild(radio)

      row.appendChild(el(doc, 'span', {
        className: 'stoq-preorder-payment-title',
        text: option.title,
      }))

      if (option.discountLabel !== null) {
        row.appendChild(el(doc, 'span', {
          className: 'stoq-preorder-discount-badge',
          text: option.discountLabel,
        }))
      }
      optionRoot.appendChild(row)

      if (option.description !== null) {
        optionRoot.appendChild(el(doc, 'div', {
          className: 'stoq-preorder-payment-description',
          text: option.description,
        }))
      }

      optionRoots.set(option.sellingPlanId, optionRoot)
      paymentWidget!.appendChild(optionRoot)
    })

    dialog.appendChild(paymentWidget)
  }

  // --- Acknowledgement -------------------------------------------------------
  if (requiresAcknowledgement) {
    const ack = el(doc, 'label', { className: 'stoq-preorder-ack' })
    const checkbox = el(doc, 'input', { attrs: { type: 'checkbox' } })
    checkbox.addEventListener('change', () => {
      acknowledged = checkbox.checked
      update()
    })
    ack.appendChild(checkbox)
    // Merchant-configured HTML (may contain a terms link), same as the embed.
    ack.appendChild(el(doc, 'span', {
      html: acknowledgementText(plan, settings, locale),
    }))
    dialog.appendChild(ack)
  }

  // --- Actions ----------------------------------------------------------------
  const actions = el(doc, 'div', { className: 'stoq-preorder-actions' })
  const confirmButton = el(doc, 'button', {
    className: 'stoq-preorder-confirm',
    text: texts.continueLabel,
    attrs: { type: 'button' },
  })
  confirmButton.style.color = styles.buttonTextColor
  confirmButton.style.backgroundColor = styles.buttonBackgroundColor
  confirmButton.addEventListener('click', confirm)
  actions.appendChild(confirmButton)

  const cancelButton = el(doc, 'button', {
    className: 'stoq-preorder-cancel',
    text: texts.cancelLabel,
    attrs: { type: 'button' },
  })
  cancelButton.addEventListener('click', reject)
  actions.appendChild(cancelButton)
  dialog.appendChild(actions)

  function update(opts: { skipInput?: boolean } = {}): void {
    if (!opts.skipInput) qtyInput.value = String(quantity)
    if (error !== null) {
      errorEl.textContent = error
      errorEl.style.display = ''
    } else {
      errorEl.textContent = ''
      errorEl.style.display = 'none'
    }
    decrement.disabled = quantity <= bounds.min
    increment.disabled = bounds.max !== null && quantity >= bounds.max
    confirmButton.disabled = error !== null || (requiresAcknowledgement && !acknowledged)
    for (const [optionId, root] of optionRoots) {
      root.classList.toggle('stoq-preorder-payment-option--selected', optionId === selectedId)
    }
  }

  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) reject()
  })
  doc.addEventListener('keydown', onKeydown, true)

  overlay.appendChild(dialog)
  doc.body.appendChild(overlay)
  activeClose = close

  update()
  emit(EVENT_PREORDER_MODAL_OPENED, { productData, variantId, sellingPlan: plan })
  dialog.focus()
}
